import "@/App.css";
import { useEffect, useRef, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import AppShell from "@/components/AppShell";
import Dashboard from "@/pages/Dashboard";
import NovaPesagem from "@/pages/NovaPesagem";
import Historico from "@/pages/Historico";
import AnaliseVideo from "@/pages/AnaliseVideo";
import ShareAnalysis from "@/pages/ShareAnalysis";
import Cadastros from "@/pages/Cadastros";
import Perfil from "@/pages/Perfil";
import Upgrade from "@/pages/Upgrade";
import Ajuda from "@/pages/Ajuda";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import AdminShell from "@/components/AdminShell";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminPlans from "@/pages/admin/AdminPlans";
import AdminFinance from "@/pages/admin/AdminFinance";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminOrganizations from "@/pages/admin/AdminOrganizations";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminSupport from "@/pages/admin/AdminSupport";
import AdminFaq from "@/pages/admin/AdminFaq";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AuthCallback() {
  const { exchangeGoogleSession } = useAuth();
  const navigate = useNavigate();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;
    (async () => {
      if (sessionId) {
        try {
          await exchangeGoogleSession(sessionId);
        } catch {
          /* fall through to login */
        }
      }
      window.history.replaceState(null, "", "/app");
      navigate("/app", { replace: true });
    })();
  }, [exchangeGoogleSession, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gado-cream">
      <div className="text-gado-muted font-satoshi">Entrando…</div>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gado-cream">
        <div className="h-8 w-8 rounded-full border-2 border-gado-green border-t-transparent animate-spin" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/share/analise/:token" element={<ShareAnalysis />} />
      <Route
        path="/app"
        element={
          <Protected>
            <AppShell />
          </Protected>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="nova-pesagem" element={<NovaPesagem />} />
        <Route path="analise-video" element={<AnaliseVideo />} />
        <Route path="historico" element={<Historico />} />
        <Route path="cadastros" element={<Cadastros />} />
        <Route path="perfil" element={<Perfil />} />
        <Route path="upgrade" element={<Upgrade />} />
        <Route path="ajuda" element={<Ajuda />} />
      </Route>
      <Route
        path="/admin"
        element={
          <Protected>
            <AdminShell />
          </Protected>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="organizacoes" element={<AdminOrganizations />} />
        <Route path="usuarios" element={<AdminUsers />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="planos" element={<AdminPlans />} />
        <Route path="financeiro" element={<AdminFinance />} />
        <Route path="suporte" element={<AdminSupport />} />
        <Route path="faq" element={<AdminFaq />} />
        <Route path="auditoria" element={<AdminAudit />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
